"use client";

import React, { useState } from 'react';
import { Typography } from '@/components/ui/Typography';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Mail, MessageSquare, AlertCircle } from 'lucide-react';

export function ContactForm() {
  const [formData, setFormData] = useState({ name: '', email: '', subject: 'general', message: '' });
  const [status, setStatus] = useState('idle'); // idle, submitting, success

  const handleSubmit = (e) => {
    e.preventDefault();
    setStatus('submitting');
    
    // Simulate form submission. In a real app, send to an API route or service like Formspree/Firebase.
    setTimeout(() => {
      setStatus('success');
      setFormData({ name: '', email: '', subject: 'general', message: '' });
    }, 1500);
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
      
      {/* Contact Info Sidebar */}
      <div className="md:col-span-1 space-y-8">
        <div className="bg-secondary/20 p-6 rounded-2xl border border-border">
          <Mail className="w-8 h-8 text-primary mb-4" />
          <h2 className="font-semibold text-lg mb-2">Email Us</h2>
          <p className="text-sm text-foreground/70 mb-4">For general inquiries and support.</p>
          <a href="mailto:lyricsverseteam@gmail.com" className="font-medium text-primary hover:underline">lyricsverseteam@gmail.com</a>
        </div>

        <div className="bg-secondary/20 p-6 rounded-2xl border border-border">
          <AlertCircle className="w-8 h-8 text-primary mb-4" />
          <h2 className="font-semibold text-lg mb-2">Corrections & DMCA</h2>
          <p className="text-sm text-foreground/70 mb-4">Found a mistake or need to file a copyright claim?</p>
          <a href="mailto:lyricsverseteam@gmail.com" className="font-medium text-primary hover:underline">lyricsverseteam@gmail.com</a>
        </div>
      </div>

      {/* Contact Form */}
      <div className="md:col-span-2 bg-card p-6 md:p-8 rounded-2xl border border-border shadow-sm">
        {status === 'success' ? (
          <div className="h-full flex flex-col items-center justify-center text-center py-12 space-y-4">
            <div className="w-16 h-16 bg-green-500/20 text-green-500 rounded-full flex items-center justify-center">
              <MessageSquare className="w-8 h-8" />
            </div>
            <Typography variant="h3">Message Sent!</Typography>
            <p className="text-foreground/70">Thank you for reaching out. We will get back to you shortly.</p>
            <Button onClick={() => setStatus('idle')} variant="outline" className="mt-4">
              Send Another Message
            </Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <label htmlFor="name" className="text-sm font-medium">Your Name</label>
                <Input 
                  id="name" 
                  required 
                  value={formData.name} 
                  onChange={e => setFormData({...formData, name: e.target.value})} 
                />
              </div>
              <div className="space-y-2">
                <label htmlFor="email" className="text-sm font-medium">Your Email</label>
                <Input 
                  id="email" 
                  type="email" 
                  required 
                  value={formData.email} 
                  onChange={e => setFormData({...formData, email: e.target.value})} 
                />
              </div>
            </div>

            <div className="space-y-2">
              <label htmlFor="subject" className="text-sm font-medium">Subject</label>
              <select 
                id="subject"
                className="flex h-10 w-full rounded-md border border-border bg-transparent px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                value={formData.subject}
                onChange={e => setFormData({...formData, subject: e.target.value})}
              >
                <option value="general" className="bg-background">General Inquiry</option>
                <option value="correction" className="bg-background">Lyric Correction</option>
                <option value="request" className="bg-background">Song Request</option>
                <option value="legal" className="bg-background">Legal / DMCA</option>
              </select>
            </div>

            <div className="space-y-2">
              <label htmlFor="message" className="text-sm font-medium">Your Message</label>
              <textarea 
                id="message" 
                required 
                className="flex min-h-[150px] w-full rounded-md border border-border bg-transparent px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                value={formData.message}
                onChange={e => setFormData({...formData, message: e.target.value})}
              />
            </div>

            <Button type="submit" disabled={status === 'submitting'} className="w-full md:w-auto">
              {status === 'submitting' ? 'Sending...' : 'Send Message'}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
